import React, { useCallback, useEffect, useRef, useState } from 'react';
import apiService from './apiService';
import authService from './authService';
import authServiceDriver from './authServiceDriver';

/**
 * Acceptation obligatoire des documents légaux (sites client et chauffeur).
 * Dès qu'une session est ouverte, le serveur indique si la version en vigueur
 * a été acceptée ; sinon fenêtre bloquante jusqu'à « J'accepte » ou déconnexion.
 * Ne bloque jamais si le serveur est injoignable, ni si la version demandée
 * n'est pas celle que ce site affiche.
 */
export const LEGAL_VERSION = '2.0';
const WATCH_MS = 5000;

const DOCS = [
  { href: '/cgu', label: "Conditions générales d'utilisation" },
  { href: '/privacy', label: 'Politique de confidentialité' },
  { href: '/remboursement', label: 'Annulation et remboursement' },
];

export default function LegalGate({ userType = 'user', children }) {
  const [visible, setVisible] = useState(false);
  const [checked, setChecked] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const checkedToken = useRef(null);

  const call = useCallback(async (path, options = {}) => {
    const token = apiService.getToken(userType);
    if (!token) return null;
    const res = await fetch(`${apiService.baseURL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'X-Mbiyo-App': 'web',
        Authorization: `Bearer ${token}`,
      },
      cache: 'no-store',
    });
    const json = await res.json().catch(() => ({}));
    return { status: res.status, json };
  }, [userType]);

  const check = useCallback(async () => {
    const token = apiService.getToken(userType);
    if (!token) {
      checkedToken.current = null;
      setVisible(false);
      return;
    }
    if (checkedToken.current === token) return;
    checkedToken.current = token;
    try {
      const r = await call('/legal/status');
      const s = r?.json;
      setVisible(!!(r?.status === 200 && s?.success && s.required && s.current === LEGAL_VERSION));
    } catch (_) {
      checkedToken.current = null;
    }
  }, [call, userType]);

  useEffect(() => {
    check();
    const id = setInterval(check, WATCH_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, [check]);

  const accept = async () => {
    if (!checked) { setError('Cochez la case pour confirmer que vous acceptez.'); return; }
    setSending(true);
    setError('');
    try {
      const r = await call('/legal/accept', { method: 'POST', body: JSON.stringify({ version: LEGAL_VERSION, app: 'web' }) });
      if ((r?.status === 200 && r.json?.success) || r?.status === 409 || r === null) setVisible(false);
      else setError("Impossible d'enregistrer votre acceptation. Vérifiez votre connexion et réessayez.");
    } catch (_) {
      setError("Impossible d'enregistrer votre acceptation. Vérifiez votre connexion et réessayez.");
    } finally {
      setSending(false);
    }
  };

  const signOut = () => {
    // Sécurité (2026-11-01) : vraie déconnexion (jeton d'appareil révoqué côté serveur et supprimé) —
    // avant, seul le jeton d'accès partait et la session se rouvrait toute seule au rechargement.
    try { (userType === 'driver' ? authServiceDriver : authService).logout(); } catch (_) {}
    window.location.reload();
  };

  return (
    <>
      {children}
      {visible && (
        <div className="mb-legal-overlay" role="dialog" aria-modal="true" aria-labelledby="mb-legal-title">
          <div className="mb-legal-card">
            <div className="mb-legal-badge">📜</div>
            <h2 id="mb-legal-title">Nos conditions évoluent</h2>
            <p className="mb-legal-intro">
              Nous avons mis à jour nos Conditions générales d'utilisation, notre Politique de confidentialité et notre
              Politique d'annulation et de remboursement. Pour continuer à utiliser Mbiyo, lisez-les puis acceptez-les.
            </p>
            <div className="mb-legal-docs">
              {DOCS.map((d) => (
                <a key={d.href} href={d.href} target="_blank" rel="noopener noreferrer">
                  <span>{d.label}</span><span className="mb-legal-read">Lire ↗</span>
                </a>
              ))}
            </div>
            <label className="mb-legal-check">
              <input type="checkbox" checked={checked} onChange={(e) => { setChecked(e.target.checked); setError(''); }} />
              <span>J'ai lu et j'accepte les Conditions générales d'utilisation et la Politique de confidentialité.</span>
            </label>
            {error && <p className="mb-legal-error">{error}</p>}
            <button type="button" className="mb-legal-accept" onClick={accept} disabled={sending} aria-disabled={!checked}>
              {sending ? 'Enregistrement…' : "J'accepte"}
            </button>
            <button type="button" className="mb-legal-logout" onClick={signOut}>Se déconnecter</button>
          </div>
          <style>{`
            .mb-legal-overlay{position:fixed;inset:0;z-index:10000;background:rgba(10,10,10,.72);display:flex;align-items:center;justify-content:center;padding:16px;overscroll-behavior:contain}
            .mb-legal-card{background:#fff;color:#1f2328;width:100%;max-width:480px;max-height:calc(100dvh - 32px);overflow:auto;border-radius:20px;padding:26px 22px 18px;box-shadow:0 24px 60px rgba(0,0,0,.35)}
            .mb-legal-badge{width:56px;height:56px;border-radius:16px;background:#F54119;display:flex;align-items:center;justify-content:center;font-size:28px;margin:0 auto 14px}
            .mb-legal-card h2{text-align:center;font-size:1.35rem;font-weight:800;margin:0 0 8px}
            .mb-legal-intro{text-align:center;color:#5e6470;font-size:.95rem;line-height:1.55;margin:0 0 16px}
            .mb-legal-docs{display:flex;flex-direction:column;gap:8px;margin-bottom:14px}
            .mb-legal-docs a{display:flex;justify-content:space-between;align-items:center;gap:10px;border:1px solid #ece8e2;border-radius:12px;padding:12px 14px;color:#1f2328;text-decoration:none;font-weight:700;font-size:.95rem}
            .mb-legal-read{color:#F54119;font-weight:800;white-space:nowrap}
            .mb-legal-check{display:flex;gap:10px;align-items:flex-start;font-size:.92rem;line-height:1.45;font-weight:600;cursor:pointer;margin:6px 0 10px}
            .mb-legal-check input{width:20px;height:20px;margin-top:1px;accent-color:#F54119;flex:none}
            .mb-legal-error{color:#c62828;font-size:.88rem;margin:0 0 10px}
            .mb-legal-accept{width:100%;height:50px;border:none;border-radius:14px;background:#F54119;color:#fff;font-weight:800;font-size:1rem;cursor:pointer;touch-action:manipulation;transition:transform .12s ease-out,opacity .12s}
            .mb-legal-accept[aria-disabled="true"]{opacity:.5}
            .mb-legal-accept:active{transform:scale(.98)}
            .mb-legal-logout{width:100%;background:none;border:none;color:#5e6470;font-weight:700;padding:12px;cursor:pointer;font-size:.92rem}
            @media (hover:hover) and (pointer:fine){.mb-legal-docs a:hover{border-color:#F54119}}
          `}</style>
        </div>
      )}
    </>
  );
}
